// ============================================================
// Analytics v2 — SQL generation for the logical events table
//
// Two dialects with one code path:
//   ae      Workers Analytics Engine SQL API (ClickHouse-flavoured, sampled:
//           every count/sum is weighted by _sample_interval)
//   sqlite  D1 / SQLite `events` table (local dev, self-host, tests)
//
// The AE SQL API has no bind parameters, so every literal goes through
// dialect.lit(), which rejects control characters and escapes quotes.
// ============================================================
import { dimensionSlot, metricSlot } from '../config.js';
import { AI_CRAWL_EVENT } from '../ai.js';
import { CLICK_ID_SHIFT, CLICK_ID_TYPES, isClickIdType } from '../click-ids.js';
import { aeColumn } from '../schema.js';
export class QueryInputError extends Error {
}
// Printable characters only; quotes/backslashes are escaped per dialect.
// eslint-disable-next-line no-control-regex -- rejecting control characters is the point
const SAFE_LITERAL = /^[^\u0000-\u001f\u007f]{0,300}$/;
function checkLiteral(v) {
    if (typeof v !== 'string' || !SAFE_LITERAL.test(v)) {
        throw new QueryInputError('unsupported characters in a filter value');
    }
}
export function aeDialect(dataset) {
    const lit = (v) => {
        checkLiteral(v);
        return `'${v.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
    };
    const W = '_sample_interval';
    return {
        name: 'ae',
        table: dataset,
        col: aeColumn,
        weight: W,
        lit,
        countIf: cond => `sumIf(${W}, ${cond})`,
        sumIf: (expr, cond) => `sumIf(${expr} * ${W}, ${cond})`,
        // AE has no NULL-skipping distinct; '' is folded in and subtracted when present.
        uniqIf: (col, cond) => `(count(DISTINCT if((${cond}) AND ${col} != '', ${col}, '')) - if(countIf(NOT ((${cond}) AND ${col} != '')) > 0, 1, 0))`,
        ifExpr: (cond, a, b) => `if(${cond}, ${a}, ${b})`,
        tsMs: 'toUnixTimestamp(timestamp) * 1000',
        timeRange: (_f, _t, from, to, tz) => `timestamp >= toDateTime(${lit(`${from} 00:00:00`)}, ${lit(tz)}) AND timestamp < toDateTime(${lit(`${nextDay(to)} 00:00:00`)}, ${lit(tz)})`,
        since: minutes => `timestamp > now() - INTERVAL '${Math.floor(minutes)}' MINUTE`,
        bucket: (interval, tz) => {
            const t = lit(tz);
            switch (interval) {
                case 'hour':
                    return `formatDateTime(toStartOfInterval(timestamp, INTERVAL '1' HOUR, ${t}), '%Y-%m-%d %H:00', ${t})`;
                case 'week':
                    return `formatDateTime(toStartOfInterval(timestamp, INTERVAL '7' DAY, ${t}), '%Y-%m-%d', ${t})`;
                case 'month':
                    return `formatDateTime(toStartOfInterval(timestamp, INTERVAL '1' MONTH, ${t}), '%Y-%m', ${t})`;
                default:
                    return `formatDateTime(toStartOfInterval(timestamp, INTERVAL '1' DAY, ${t}), '%Y-%m-%d', ${t})`;
            }
        },
        supportsQuantiles: true,
        quantile: (q, col) => `quantileExactWeighted(${q})(${col}, ${W})`,
    };
}
export function sqliteDialect(table = 'events') {
    const lit = (v) => {
        checkLiteral(v);
        return `'${v.replace(/'/g, "''")}'`;
    };
    const col = (c) => c;
    return {
        name: 'sqlite',
        table,
        col,
        weight: '1',
        lit,
        countIf: cond => `SUM(CASE WHEN ${cond} THEN 1 ELSE 0 END)`,
        sumIf: (expr, cond) => `SUM(CASE WHEN ${cond} THEN ${expr} ELSE 0 END)`,
        uniqIf: (c, cond) => `COUNT(DISTINCT CASE WHEN (${cond}) AND ${c} != '' THEN ${c} END)`,
        ifExpr: (cond, a, b) => `(CASE WHEN ${cond} THEN ${a} ELSE ${b} END)`,
        tsMs: 'timestamp',
        timeRange: (fromMs, toMs) => `timestamp >= ${Math.floor(fromMs)} AND timestamp < ${Math.floor(toMs)}`,
        since: (minutes, nowMs) => `timestamp > ${Math.floor(nowMs - minutes * 60_000)}`,
        bucket: (interval, _tz, offsetMinutes) => {
            const mod = `'${offsetMinutes >= 0 ? '+' : ''}${offsetMinutes} minutes'`;
            const fmt = interval === 'hour' ? '%Y-%m-%d %H:00' : interval === 'month' ? '%Y-%m' : '%Y-%m-%d';
            if (interval === 'week') {
                // 7-day buckets counted from 1970-01-01 in local time, exactly like AE's
                // toStartOfInterval(…, INTERVAL '7' DAY, tz), so merged series share keys.
                return `strftime('%Y-%m-%d', ((timestamp / 1000 + ${offsetMinutes * 60}) / 604800) * 604800, 'unixepoch')`;
            }
            return `strftime('${fmt}', timestamp / 1000, 'unixepoch', ${mod})`;
        },
        supportsQuantiles: false,
        quantile: () => {
            throw new QueryInputError('quantiles are not supported by this store');
        },
    };
}
// --- Dates & time zones ---
const SAFE_DATE = /^\d{4}-\d{2}-\d{2}$/;
const SAFE_TZ = /^[A-Za-z_]+(\/[A-Za-z0-9_+-]+){0,2}$/;
export function nextDay(date) {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
}
export function addDays(date, days) {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
export function daysBetween(from, to) {
    return (Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) +
        1);
}
/** Offset of `tz` from UTC at instant `ms`, in minutes. */
export function tzOffsetMinutes(tz, ms) {
    if (tz === 'UTC') {
        return 0;
    }
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(ms));
    const get = (t) => Number(parts.find(p => p.type === t)?.value);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60_000);
}
/** Epoch ms of local midnight of `date` in `tz`. */
export function zonedMidnightMs(date, tz) {
    const utcMidnight = Date.parse(`${date}T00:00:00Z`);
    const offset = tzOffsetMinutes(tz, utcMidnight);
    const guess = utcMidnight - offset * 60_000;
    // Re-evaluate the offset at the guessed instant (DST edges).
    return utcMidnight - tzOffsetMinutes(tz, guess) * 60_000;
}
export function previousPeriod(from, to) {
    const len = daysBetween(from, to);
    const end = addDays(from, -1);
    return { from: addDays(end, -(len - 1)), to: end };
}
/** Public dimension names → logical columns. `d:<name>` resolves via project config. */
export const STANDARD_DIMENSIONS = {
    event: 'event',
    sub_tenant: 'sub_tenant',
    host: 'host',
    path: 'path',
    referrer: 'referrer',
    channel: 'channel',
    utm_source: 'utm_source',
    utm_campaign: 'utm_campaign',
    country: 'country',
    device: 'device',
    browser: 'browser',
    os: 'os',
    item_id: 'item_id',
    label: 'label',
};
export function resolveDimension(name, config) {
    if (typeof name !== 'string') {
        throw new QueryInputError('dimension must be a string');
    }
    // Own keys only: 'constructor' / '__proto__' must not resolve to Object.prototype members.
    if (Object.hasOwn(STANDARD_DIMENSIONS, name)) {
        return STANDARD_DIMENSIONS[name];
    }
    if (name.startsWith('d:')) {
        const slot = dimensionSlot(config, name.slice(2));
        if (slot) {
            return `d${slot}`;
        }
    }
    throw new QueryInputError(`unknown dimension '${name}'`);
}
export function resolveNumericField(name, config) {
    if (name === 'value' || name === 'qty' || name === 'duration_ms') {
        return name;
    }
    if (name.startsWith('m:')) {
        const slot = metricSlot(config, name.slice(2));
        if (slot) {
            return `m${slot}`;
        }
    }
    throw new QueryInputError(`unknown numeric field '${name}'`);
}
/** Pseudo-dimension: click-id TYPE present on the landing (click-ids.ts, bits of `reserved`). */
export const CLICK_ID_DIMENSION = 'click_id';
/** Row condition "click id `type` was present". Arithmetic bit test: AE SQL has no bitAnd. */
export function clickIdSql(d, type) {
    if (!isClickIdType(type)) {
        throw new QueryInputError(`unknown click id '${type}' (${CLICK_ID_TYPES.join(', ')})`);
    }
    const r = d.col('reserved');
    const p = CLICK_ID_SHIFT * 2 ** CLICK_ID_TYPES.indexOf(type);
    return d.name === 'ae'
        ? `(floor(${r} / ${p}) - 2 * floor(${r} / ${p * 2})) = 1`
        : `((CAST(${r} AS INTEGER) / ${p}) % 2) = 1`;
}
function clickIdFilterSql(d, f) {
    switch (f.op) {
        case 'eq':
            return clickIdSql(d, f.value);
        case 'neq':
            return `NOT (${clickIdSql(d, f.value)})`;
        case 'in': {
            const values = f.value.split('|').filter(Boolean).slice(0, CLICK_ID_TYPES.length);
            if (values.length === 0) {
                throw new QueryInputError(`empty 'in' filter on ${CLICK_ID_DIMENSION}`);
            }
            return `(${values.map(v => clickIdSql(d, v)).join(' OR ')})`;
        }
        default:
            throw new QueryInputError(`${CLICK_ID_DIMENSION} supports eq, neq and in`);
    }
}
export function filterSql(d, f, config) {
    if (f.dim === CLICK_ID_DIMENSION) {
        return clickIdFilterSql(d, f);
    }
    const col = d.col(resolveDimension(f.dim, config));
    switch (f.op) {
        case 'eq':
            return `${col} = ${d.lit(f.value)}`;
        case 'neq':
            return `${col} != ${d.lit(f.value)}`;
        case 'prefix':
            return d.name === 'ae'
                ? `startsWith(${col}, ${d.lit(f.value)})`
                : `substr(${col}, 1, ${f.value.length}) = ${d.lit(f.value)}`;
        case 'in': {
            const values = f.value.split('|').filter(Boolean).slice(0, 50);
            if (values.length === 0) {
                throw new QueryInputError(`empty 'in' filter on ${f.dim}`);
            }
            return `${col} IN (${values.map(v => d.lit(v)).join(', ')})`;
        }
        default:
            throw new QueryInputError(`unsupported filter op '${String(f.op)}'`);
    }
}
/** `dim:op:value` or `d:<name>:op:value` (value may contain ':'). */
export function parseFilter(raw) {
    let dim;
    let rest;
    const first = raw.indexOf(':');
    if (raw.startsWith('d:')) {
        const second = raw.indexOf(':', 2);
        if (second < 0) {
            throw new QueryInputError(`bad filter '${raw}'`);
        }
        dim = raw.slice(0, second);
        rest = raw.slice(second + 1);
    }
    else {
        if (first < 0) {
            throw new QueryInputError(`bad filter '${raw}' (expected dim:op:value)`);
        }
        dim = raw.slice(0, first);
        rest = raw.slice(first + 1);
    }
    const k = rest.indexOf(':');
    if (k < 0) {
        throw new QueryInputError(`bad filter '${raw}' (expected dim:op:value)`);
    }
    const op = rest.slice(0, k);
    if (!['eq', 'neq', 'prefix', 'in'].includes(op)) {
        throw new QueryInputError(`bad filter op '${op}'`);
    }
    return { dim, op, value: rest.slice(k + 1) };
}
const FILTER_OPS = ['eq', 'neq', 'prefix', 'in'];
/**
 * A filter from a JSON body: either the `dim:op:value` string or `{ dim, op, value }` with
 * string members. Anything else is a 400 (never a TypeError deeper in a store).
 */
export function toFilter(raw) {
    if (typeof raw === 'string') {
        return parseFilter(raw);
    }
    const f = raw;
    if (!f ||
        typeof f !== 'object' ||
        typeof f.dim !== 'string' ||
        typeof f.op !== 'string' ||
        !FILTER_OPS.includes(f.op) ||
        typeof f.value !== 'string') {
        throw new QueryInputError("a filter must be 'dim:op:value' or { dim, op, value } with string members");
    }
    return { dim: f.dim, op: f.op, value: f.value };
}
export const MAX_RANGE_DAYS = 1830;
export function validateRange(from, to, tz) {
    if (!SAFE_DATE.test(from) || !SAFE_DATE.test(to) || from > to) {
        throw new QueryInputError('from/to must be YYYY-MM-DD and from <= to');
    }
    if (daysBetween(from, to) > MAX_RANGE_DAYS) {
        throw new QueryInputError(`range is limited to ${MAX_RANGE_DAYS} days`);
    }
    if (!SAFE_TZ.test(tz)) {
        throw new QueryInputError('invalid tz');
    }
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
    }
    catch {
        throw new QueryInputError('unknown tz');
    }
}
/** Excludes AI crawler rows (ai.ts) — they are never traffic. */
export function notAiCrawl(d) {
    return `${d.col('event')} != ${d.lit(AI_CRAWL_EVENT)}`;
}
/**
 * Base WHERE (project, time range, sub-tenant, internal traffic, filters) + extras.
 * AI crawler rows are excluded unless `opts.aiCrawl` (only the ai-crawlers report sets it);
 * privacy-drop markers (internal) unless `opts.privacyMarkers` (only the evidence report).
 */
export function baseWhere(d, p, config, extra = [], opts = {}) {
    const from = opts.from ?? p.from;
    const to = opts.to ?? p.to;
    const clauses = [
        `${d.col('project_id')} = ${d.lit(p.projectId)}`,
        d.timeRange(zonedMidnightMs(from, p.tz), zonedMidnightMs(nextDay(to), p.tz), from, to, p.tz),
    ];
    if (!opts.aiCrawl) {
        clauses.push(notAiCrawl(d));
    }
    if (p.subTenant !== null) {
        clauses.push(`${d.col('sub_tenant')} = ${d.lit(p.subTenant)}`);
    }
    if (!p.includeInternal) {
        clauses.push(`${d.col('internal')} = 0`);
    }
    else if (!opts.privacyMarkers) {
        // Privacy-evidence markers are internal rows, never traffic (privacy.ts).
        clauses.push(`${d.col('event')} != '_privacy_drop'`);
    }
    for (const f of p.filters) {
        clauses.push(filterSql(d, f, config));
    }
    if (p.excludeVisitors && p.excludeVisitors.length > 0) {
        clauses.push(`${d.col('visitor')} NOT IN (${p.excludeVisitors.map(v => d.lit(v)).join(', ')})`);
    }
    return `WHERE ${[...clauses, ...extra].join(' AND ')}`;
}
/** Conditions of a matcher other than its event name. */
export function matcherConditions(d, m, config) {
    const parts = [];
    if (m.label !== undefined) {
        parts.push(`${d.col('label')} = ${d.lit(m.label)}`);
    }
    if (m.item_id !== undefined) {
        parts.push(`${d.col('item_id')} = ${d.lit(m.item_id)}`);
    }
    if (m.path_prefix !== undefined) {
        parts.push(filterSql(d, { dim: 'path', op: 'prefix', value: m.path_prefix }, config));
    }
    if (m.server !== undefined) {
        parts.push(`${d.col('server')} = ${m.server ? 1 : 0}`);
    }
    for (const f of m.where ?? []) {
        parts.push(filterSql(d, f, config));
    }
    return parts;
}
export function matcherSql(d, m, config) {
    const { event, ...rest } = m;
    return `(${[`${d.col('event')} = ${d.lit(event)}`, ...matcherConditions(d, rest, config)].join(' AND ')})`;
}
const EVENT_RE = /^[a-z][a-z0-9_]{0,63}$/;
export function parseMatcher(raw, what = 'matcher') {
    if (typeof raw === 'string') {
        if (!EVENT_RE.test(raw)) {
            throw new QueryInputError(`${what}: invalid event name`);
        }
        return { event: raw };
    }
    if (!raw || typeof raw !== 'object') {
        throw new QueryInputError(`${what} must be an event name or object`);
    }
    const m = raw;
    if (typeof m.event !== 'string' || !EVENT_RE.test(m.event)) {
        throw new QueryInputError(`${what}.event invalid`);
    }
    const out = { event: m.event };
    for (const k of ['label', 'item_id', 'path_prefix']) {
        if (m[k] !== undefined) {
            if (typeof m[k] !== 'string') {
                throw new QueryInputError(`${what}.${k} must be a string`);
            }
            out[k] = m[k];
        }
    }
    if (m.server !== undefined) {
        out.server = m.server === true;
    }
    if (m.where !== undefined) {
        if (!Array.isArray(m.where)) {
            throw new QueryInputError(`${what}.where must be an array`);
        }
        out.where = m.where.map(toFilter);
    }
    return out;
}
//# sourceMappingURL=sql.js.map